/**
 * @file This is the main page component for displaying an order's verification images.
 * It orchestrates the data fetching and UI rendering.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useOrder } from './hooks/useOrder';
import { useMetaTags } from './hooks/useMetaTags';
import ImageGrid from './components/ImageGrid';
import Loader from './components/Loader';
import ErrorMessage from './components/ErrorMessage';
import CustomerInfo from './components/CustomerInfo';

const OrderPage = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const { images, customer, isLoading, error, total, retry } = useOrder(orderId);

    // Preparar meta tags basados en los datos
    const metaTitle = 'Florentina Garden - Confirmación de pedido';

    const metaDescription = !isLoading && !error && customer ?
        `Pedido de ${customer.name}. Total: $${total.toFixed(2)}. Confirmación de imágenes del pedido.` :
        'Florentina Garden - Confirmación de pedido';

    const metaImage = !isLoading && !error && images.length > 0 ?
        images[0].url :
        undefined;

    // Usar el hook de meta tags
    useMetaTags({
        title: metaTitle,
        description: metaDescription,
        image: metaImage,
        url: window.location.href,
        type: 'article'
    });

    const renderContent = () => {
        if (isLoading) {
            return React.createElement(Loader);
        }

        if (error) {
            return React.createElement(ErrorMessage, {
                message: error.message,
                onRetry: retry
            });
        }

        return React.createElement(ImageGrid, { images });
    };

    return React.createElement('div', { className: 'container' },
        React.createElement('div', { className: 'order-header' },
            React.createElement('h1', null, 'Verificación de Pedido')
        ),

        !isLoading && !error && customer && React.createElement(CustomerInfo, { customer }),

        React.createElement('div', { className: 'order-id-section' },
            React.createElement('p', { className: 'order-id' }, `ID del Pedido: ${orderId}`)
        ),

        renderContent(),

        !isLoading && !error && React.createElement('div', { className: 'order-total' },
            React.createElement('h3', null, `Total: $${total.toFixed(2)}`)
        )
    );
};

export default OrderPage;
